import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Chat as ChatIcon,
} from '@mui/icons-material';
import EntityChannelList, { EntityChannel } from './EntityChannelList';
import CustomChatView from './CustomChatView';
import NewChatDialog from './NewChatDialog';
import { useUser } from '../../contexts/UserContext';

const ChatPage: React.FC = () => {
  const { currentUser } = useUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [selectedChannel, setSelectedChannel] = useState<EntityChannel | null>(null);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);

  const handleChannelSelect = (channel: EntityChannel) => {
    setSelectedChannel(channel);
  };

  const handleNewChat = () => {
    setShowNewChatDialog(true);
  };

  const handleCloseNewChat = () => {
    setShowNewChatDialog(false);
  };

  if (!currentUser) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Please sign in to access chat
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You need to be logged in to view and participate in conversations.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2, height: '100vh' }}>
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Channel List */}
        <Grid item xs={12} md={4} lg={3}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" gutterBottom>
                Conversations
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewChat}
                size="small"
                fullWidth
              >
                New Chat
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              <EntityChannelList
                onChannelSelect={handleChannelSelect}
                activeChannelId={selectedChannel?.id}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Chat View */}
        <Grid item xs={12} md={8} lg={9}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedChannel ? (
              <CustomChatView
                room={{
                  id: selectedChannel.id,
                  name: selectedChannel.entityName,
                  description: `${selectedChannel.type} chat`,
                  createdBy: '',
                  createdAt: selectedChannel.createdAt || new Date(),
                  members: [], // This would need to be populated based on your data structure
                  isPrivate: false,
                  avatarUrl: selectedChannel.avatarUrl,
                }}
                onBack={isMobile ? () => setSelectedChannel(null) : undefined}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  p: 4,
                }}
              >
                <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Select a conversation
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Choose a conversation from the list to start chatting, or create a new one.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* New Chat Dialog */}
      <NewChatDialog
        open={showNewChatDialog}
        onClose={handleCloseNewChat}
      />
    </Container>
  );
};

export default ChatPage;
